/**
 * The one in-flight batch-sign run, held outside React and persisted.
 *
 * NOT PAGE STATE: holding the cursor in `/refills` would restart or abandon
 * the run on navigation, and a half-finished run means prescriptions signed
 * and never issued. Same shape as `projectsBackgroundRegistry`.
 *
 * ONE RUN, NOT A REGISTRY — the eCW overlay takes one agent at a time, so a
 * concurrent run is a state to refuse rather than to model.
 *
 * PERSISTED BUT NEVER SELF-RESUMING; see `armed` below.
 */

import { getCurrentUser } from '@/hooks/query/current-user-cache';
import { logger } from '@/utils/logger/logger';
import {
  REFILL_BATCH_ROW_STATES,
  type RefillBatchRowState,
} from '@heidi/agents/contracts';
import { buildClinicalTasksStorageScope } from '@heidi/tasks/contracts';
import { getWorkspaceId } from '@heidi/workspace/data';
// Generic in behaviour, specific only in name: the payload is the sender's
// session id, so it means "some persisted registry changed, re-read yours".
// Riding it adds no Tauri surface (`@tauri-apps/*` is frozen for new files
// under `src/**`). `storage` is unreliable across Tauri webviews.
import type { UnlistenFn } from '@heidi/plugin-core';
import {
  emitProjectAgentRegistryPoke,
  listenProjectAgentRegistryPoke,
} from './project-agent-ipc';

export interface RefillBatchRunRecord {
  runId: string;
  /**
   * The ids the run walks, in order, frozen when the clinician attested. Held
   * ids are NOT in here — they were never part of the run.
   */
  requestIds: readonly string[];
  /** Held back at pre-flight: excluded from the run, left in the queue. */
  heldIds: readonly string[];
  /**
   * Index into {@link requestIds} of the next script to work, so it doubles as
   * the resume point after a stop. Equal to `requestIds.length` when done.
   */
  cursor: number;
  /** Per-request result. Absent means not yet worked. */
  outcomes: Readonly<Record<string, RefillBatchRowState>>;
  /**
   * The clinician asked to stop after the script in flight. Graceful, at a
   * script boundary — there is no recalling a prompt the agent has started.
   */
  stopRequested: boolean;
  startedAt: number;
  updatedAt: number;
}

/**
 * The record plus whether this session may act on it.
 *
 * `armed` is NOT persisted, which is what makes writing the rest to disk
 * acceptable: a reload rehydrates the cursor and outcomes so the board can
 * report, and comes back disarmed. A record that armed itself on restore
 * would finish the queue with nobody watching, prescribing with no undo.
 *
 * Navigation disarms nothing: this module outlives the route.
 */
export interface RefillBatchRunSnapshot {
  run: RefillBatchRunRecord | null;
  armed: boolean;
}

// UNCARVED DOMAIN KEY (rpa, AGE-2370): rpa has no workspace package, so there
// is no /contracts door to declare this in — the four sibling rpa keys are
// declared the same way. Not a query cache identity either: it names a
// localStorage slot for the run record and is never read into a
// queryKey/mutationKey head, exactly as PROJECTS_BACKGROUND_REGISTRY_KEY sits
// beside its own registry. Retire when rpa is carved.
//
// A PREFIX, NOT THE KEY. The record says "Paused after 3 of 10" over a button
// that signs the remaining 7, so an unscoped row offers the next clinician on
// a shared workstation a run they never attested to (AGE-2370 review). The
// real key is this plus `:<workspaceId>:<userId>`.
export const REFILL_BATCH_RUN_STORAGE_KEY_PREFIX = 'refill-batch-run';

/**
 * The scoped key, or null while the identity is still resolving.
 *
 * `buildClinicalTasksStorageScope` is reused rather than re-derived — it is an
 * inert `workspaceId:userId` join on `@heidi/tasks/contracts`, and sharing it
 * is what makes this key's scope byte-identical to the one the logout sweep
 * computes. A null scope PERSISTS NOTHING rather than falling back to a shared
 * bucket, which is the same call `createClinicalTasksScopedStorage` makes and
 * for the same reason: a shared bucket is the leak this scoping exists to
 * close. The run still completes in memory; it just will not survive a reload.
 */
const scopedRunKey = (): string | null => {
  const scope = buildClinicalTasksStorageScope({
    workspaceId: getWorkspaceId(),
    userId: getCurrentUser()?.kinde_user_id,
  });
  return scope ? `${REFILL_BATCH_RUN_STORAGE_KEY_PREFIX}:${scope}` : null;
};

/** Same-window notification; the `storage` event only fires cross-window. */
export const REFILL_BATCH_RUN_CHANGED_EVENT = 'refill-batch-run-changed';

/**
 * A run older than this is dropped on read. Generous because a lockstep run
 * is hours, bounded because last week's must not wear a resume button.
 */
export const REFILL_BATCH_RUN_MAX_AGE_MS = 24 * 60 * 60 * 1000;

const EMPTY_SNAPSHOT: RefillBatchRunSnapshot = Object.freeze({
  run: null,
  armed: false,
});

type Listener = () => void;

let _run: RefillBatchRunRecord | null = null;
let _armed = false;
let _hydrated = false;
let _snapshot: RefillBatchRunSnapshot = EMPTY_SNAPSHOT;
const _listeners = new Set<Listener>();
let _stopCrossWindowSync: (() => void) | null = null;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null;

const readStringArray = (value: unknown): string[] | null => {
  if (!Array.isArray(value)) return null;
  if (!value.every((item) => typeof item === 'string' && item)) return null;
  return value as string[];
};

const readOutcomes = (value: unknown): Record<string, RefillBatchRowState> | null => {
  if (value === undefined) return {};
  if (!isRecord(value)) return null;
  const out: Record<string, RefillBatchRowState> = {};
  for (const [id, state] of Object.entries(value)) {
    // Dropped rather than coerced: reporting a prescription's result as the
    // wrong state is the failure this module exists to avoid.
    if (
      typeof state === 'string' &&
      REFILL_BATCH_ROW_STATES.includes(state as RefillBatchRowState)
    ) {
      out[id] = state as RefillBatchRowState;
    }
  }
  return out;
};

/**
 * Parse one persisted record, or reject it whole — a partially-read run would
 * show a cursor over the wrong ids. Rejecting costs a lookup in eCW; accepting
 * costs a wrong report about a prescription.
 */
const parseRun = (value: unknown): RefillBatchRunRecord | null => {
  if (!isRecord(value)) return null;
  const runId = typeof value.runId === 'string' ? value.runId : '';
  const requestIds = readStringArray(value.requestIds);
  const heldIds = readStringArray(value.heldIds ?? []);
  const outcomes = readOutcomes(value.outcomes);
  if (!runId || !requestIds || !heldIds || !outcomes) return null;
  if (typeof value.cursor !== 'number' || !Number.isInteger(value.cursor)) return null;
  if (value.cursor < 0 || value.cursor > requestIds.length) return null;
  if (typeof value.startedAt !== 'number' || typeof value.updatedAt !== 'number') {
    return null;
  }
  return {
    runId,
    requestIds,
    heldIds,
    cursor: value.cursor,
    outcomes,
    stopRequested: value.stopRequested === true,
    startedAt: value.startedAt,
    updatedAt: value.updatedAt,
  };
};

const isFresh = (run: RefillBatchRunRecord, now = Date.now()): boolean =>
  now - run.updatedAt <= REFILL_BATCH_RUN_MAX_AGE_MS;

const readStoredRun = (): RefillBatchRunRecord | null => {
  if (typeof window === 'undefined') return null;
  const key = scopedRunKey();
  if (key === null) return null;
  let raw: string | null = null;
  try {
    raw = window.localStorage.getItem(key);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const run = parseRun(JSON.parse(raw));
    if (!run || !isFresh(run)) return null;
    return run;
  } catch {
    logger.warn('[refill-batch-run] Discarded an unparseable persisted run');
    return null;
  }
};

const persist = (): void => {
  if (typeof window === 'undefined') return;
  const key = scopedRunKey();
  if (key === null) return;
  try {
    if (_run === null) window.localStorage.removeItem(key);
    // `armed` is intentionally absent from what is written — see the type.
    else window.localStorage.setItem(key, JSON.stringify(_run));
  } catch {
    // Best-effort. A run that cannot be persisted still completes in this
    // session; it just will not survive a reload.
  }
};

const recomputeSnapshot = (): void => {
  _snapshot = Object.freeze({ run: _run, armed: _armed });
};

const emit = (): void => {
  for (const listener of _listeners) listener();
};

const runsEqual = (
  a: RefillBatchRunRecord | null,
  b: RefillBatchRunRecord | null
): boolean => {
  if (a === b) return true;
  if (!a || !b) return false;
  return JSON.stringify(a) === JSON.stringify(b);
};

const hydrateIfNeeded = (): void => {
  if (_hydrated) return;
  _hydrated = true;
  _run = readStoredRun();
  // Restored disarmed, always. This is the line that stops a reload from
  // finishing someone's prescribing for them.
  _armed = false;
  recomputeSnapshot();
};

/** Re-read persisted state (another window may have written it) and notify. */
const syncFromStorage = (): void => {
  hydrateIfNeeded();
  // No scope means nothing is persisted, so there is nothing to merge FROM.
  // Reading a null here and treating it as the truth would delete the live
  // in-memory run on every notification.
  if (scopedRunKey() === null) return;
  const stored = readStoredRun();
  if (runsEqual(_run, stored)) return;
  _run = stored;
  recomputeSnapshot();
  emit();
};

const notifyOtherWindows = (): void => {
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new Event(REFILL_BATCH_RUN_CHANGED_EVENT));
  } catch {
    // Best-effort same-window notification.
  }
  void emitProjectAgentRegistryPoke();
};

const startCrossWindowSync = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  const onStorage = (event: StorageEvent) => {
    if (event.key !== scopedRunKey()) return;
    syncFromStorage();
  };
  const onLocalChange = () => syncFromStorage();

  window.addEventListener('storage', onStorage);
  window.addEventListener(REFILL_BATCH_RUN_CHANGED_EVENT, onLocalChange);

  let cancelled = false;
  let unlistenPoke: UnlistenFn | null = null;
  void listenProjectAgentRegistryPoke(syncFromStorage).then((unlisten) => {
    if (cancelled) void unlisten();
    else unlistenPoke = unlisten;
  });

  return () => {
    cancelled = true;
    window.removeEventListener('storage', onStorage);
    window.removeEventListener(REFILL_BATCH_RUN_CHANGED_EVENT, onLocalChange);
    void unlistenPoke?.();
  };
};

/**
 * Apply a change, persist it, and tell every reader — in that order.
 *
 * Re-reads storage first so a write from another window is merged rather than
 * clobbered by the whole-record persist below, exactly as the projects
 * registry does.
 */
const mutate = (
  change: (current: RefillBatchRunRecord | null) => {
    run: RefillBatchRunRecord | null;
    armed?: boolean;
  } | null
): void => {
  hydrateIfNeeded();
  // Same reason as `syncFromStorage`: with no scope the store is not the
  // source of truth, memory is, and merging a null over it would drop the run
  // between its start and its first settlement.
  if (scopedRunKey() !== null) {
    const stored = readStoredRun();
    if (!runsEqual(_run, stored)) _run = stored;
  }

  const next = change(_run);
  if (next === null) return;

  const changed = !runsEqual(_run, next.run) || (next.armed ?? _armed) !== _armed;
  _run = next.run;
  if (next.armed !== undefined) _armed = next.armed;
  if (!changed) return;

  persist();
  recomputeSnapshot();
  emit();
  notifyOtherWindows();
};

const stamp = (run: RefillBatchRunRecord): RefillBatchRunRecord => ({
  ...run,
  updatedAt: Date.now(),
});

const newRunId = (): string =>
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `refill-batch-${Date.now()}-${Math.random()}`;

export interface StartRefillBatchRunInput {
  /** Ordered, held ids already removed. */
  requestIds: readonly string[];
  heldIds?: readonly string[];
  /** Injectable for tests; production leaves it to `crypto.randomUUID`. */
  runId?: string;
}

/**
 * Begin a run, armed. Returns it, or null if it refused.
 *
 * Refuses while a run is in flight rather than replacing it: two concurrent
 * runs would both dispatch into one eCW overlay, and the loser's scripts would
 * be signed with nothing reaching the EHR.
 */
export const startRefillBatchRun = (
  input: StartRefillBatchRunInput
): RefillBatchRunRecord | null => {
  let started: RefillBatchRunRecord | null = null;
  mutate((current) => {
    if (current !== null && current.cursor < current.requestIds.length) {
      logger.warn('[refill-batch-run] Refused a second run while one is in flight', {
        pendingCount: current.requestIds.length - current.cursor,
      });
      return null;
    }
    if (input.requestIds.length === 0) return null;

    const now = Date.now();
    started = {
      runId: input.runId ?? newRunId(),
      requestIds: [...input.requestIds],
      heldIds: [...(input.heldIds ?? [])],
      cursor: 0,
      outcomes: {},
      stopRequested: false,
      startedAt: now,
      updatedAt: now,
    };
    return { run: started, armed: true };
  });
  return started;
};

/**
 * Record what became of the script at the cursor and move past it.
 *
 * Checked against the cursor as well as the id, so a late resolve from a run
 * that has moved on is dropped rather than written onto its neighbour.
 */
export const settleRefillBatchRow = (
  requestId: string,
  state: RefillBatchRowState
): void => {
  mutate((current) => {
    if (!current) return null;
    if (current.requestIds[current.cursor] !== requestId) {
      logger.warn('[refill-batch-run] Dropped a settlement for a non-current script');
      return null;
    }
    return {
      run: stamp({
        ...current,
        outcomes: { ...current.outcomes, [requestId]: state },
        cursor: current.cursor + 1,
      }),
    };
  });
};

/**
 * Record that the write for the script at the cursor was REFUSED, leaving the
 * cursor on it.
 *
 * The opposite of {@link settleRefillBatchRow} in the one way that matters: a
 * refusal is not a result for the prescription, so the row keeps its turn and
 * a resume retries it. Without this the halted row reads `Queued` and says
 * nothing about having been attempted (AGE-2370 review).
 */
export const markRefillBatchRowSkipped = (requestId: string): void => {
  mutate((current) => {
    if (!current) return null;
    if (current.requestIds[current.cursor] !== requestId) {
      logger.warn('[refill-batch-run] Dropped a skip for a non-current script');
      return null;
    }
    return {
      run: stamp({
        ...current,
        outcomes: { ...current.outcomes, [requestId]: 'skipped' },
      }),
    };
  });
};

/** The clinician's "stop after this one" — honoured at the script boundary. */
export const requestRefillBatchRunStop = (): void => {
  mutate((current) => {
    // A repeat click must not re-stamp `updatedAt`. Equality is JSON, so a
    // fresh timestamp looks like a change and notifies subscribers again.
    if (!current || current.stopRequested) return null;
    return { run: stamp({ ...current, stopRequested: true }) };
  });
};

/**
 * Pick up from the cursor and re-arm — the one way a halted run restarts.
 * Refuses a run with nothing left, so a finished one cannot be re-armed into
 * re-prescribing what it already signed.
 */
export const resumeRefillBatchRun = (): void => {
  mutate((current) => {
    if (!current || current.cursor >= current.requestIds.length) return null;
    // A `skipped` outcome on the row being retried is dropped: it described
    // the previous attempt, and leaving it would render the row as refused
    // while it is being signed.
    const retried = current.requestIds[current.cursor];
    const outcomes = { ...current.outcomes };
    if (retried !== undefined && outcomes[retried] === 'skipped')
      delete outcomes[retried];
    return {
      run: stamp({ ...current, outcomes, stopRequested: false }),
      armed: true,
    };
  });
};

/** Disarm without clearing: the run halts where it is and can be resumed. */
export const haltRefillBatchRun = (): void => {
  mutate((current) => (current ? { run: current, armed: false } : null));
};

/** Dismiss a finished run. The outcomes go with it — report before calling. */
export const clearRefillBatchRun = (): void => {
  mutate(() => ({ run: null, armed: false }));
};

export const subscribeRefillBatchRun = (listener: Listener): (() => void) => {
  hydrateIfNeeded();
  _listeners.add(listener);
  _stopCrossWindowSync ??= startCrossWindowSync();
  return () => {
    _listeners.delete(listener);
    if (_listeners.size === 0) {
      _stopCrossWindowSync?.();
      _stopCrossWindowSync = null;
    }
  };
};

export const getRefillBatchRunSnapshot = (): RefillBatchRunSnapshot => {
  hydrateIfNeeded();
  return _snapshot;
};

/** Stable server-snapshot value for `useSyncExternalStore`. */
export const getRefillBatchRunServerSnapshot = (): RefillBatchRunSnapshot =>
  EMPTY_SNAPSHOT;

export const __resetRefillBatchRunForTests = (): void => {
  _stopCrossWindowSync?.();
  _stopCrossWindowSync = null;
  _listeners.clear();
  _run = null;
  _armed = false;
  _hydrated = false;
  _snapshot = EMPTY_SNAPSHOT;
  try {
    const key = scopedRunKey();
    if (key !== null) window.localStorage.removeItem(key);
  } catch {
    // jsdom teardown.
  }
};
